/**
 * Searching a user's past chats (PLT-7658): how a query becomes words, what counts as a match, and
 * the line a result shows. Shared because both ends apply the same rule: the server decides which
 * chats match (`upserve/modules/chat/conversations.ts`), and the Chats page marks the matched words
 * in what it shows. If the two disagreed, a result could come back with nothing marked in it.
 *
 * A word matches the start of a word in the text, in any script, ignoring case: "emission" finds
 * "Emissions", "päästö" finds "päästöjen", and "3" finds "3" but not "Q3". Every word of the query
 * has to match somewhere in the chat, in its title or in any message, not necessarily in one place.
 */

/** At most this many words of a query are used; the rest are ignored, as in the decision log's search. */
export const MAX_CONVERSATION_SEARCH_WORDS = 8;

/** A query shorter than this is not searched, the threshold the top bar search uses (PLT-6813). */
export const MIN_CONVERSATION_SEARCH_LENGTH = 2;

/**
 * How much of a message the result line keeps before its first match, so the match is on screen in
 * a one-line row rather than cut off at the end of a long answer.
 */
const SNIPPET_LEAD = 40;

/** The longest result line sent. The row truncates it anyway; this only bounds the payload. */
const MAX_SNIPPET_LENGTH = 240;

/**
 * The words of a query, lower-cased. Anything that is not a letter or a digit separates words, so a
 * word is only ever letters and digits and is safe to put into a regular expression as it is, in
 * JavaScript and in Postgres alike.
 */
export function conversationSearchWords(q: string): string[] {
  return q
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(word => word.length > 0)
    .slice(0, MAX_CONVERSATION_SEARCH_WORDS);
}

/** Any of `words` at the start of a word, through to the end of that word, so the whole word is marked. */
export function conversationSearchPattern(words: string[], flags: '' | 'g' = ''): RegExp {
  return new RegExp(`(?<![\\p{L}\\p{N}])(?:${words.join('|')})[\\p{L}\\p{N}]*`, `iu${flags}`);
}

/**
 * A message as the reader saw it: the chat's inline tags (`<cite id="…"/>`, `<badge>`, `<callout>`)
 * and the markdown syntax removed, and all whitespace, line breaks included, collapsed to single
 * spaces, so it reads as one line of prose.
 */
export function conversationSearchPlainText(text: string): string {
  return text
    .replace(/<[^>]*>/g, ' ')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, '')
    .replace(/^[\s|:-]+$/gm, '')
    .replace(/[*`~|]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The line a result shows: the message with the most of the words in it (the earliest on a tie),
 * starting a few words before its first match. Null when only the title matched. `messages` are in
 * conversation order and already plain text.
 */
export function conversationSearchSnippet(messages: string[], words: string[]): string | null {
  if (words.length === 0) {
    return null;
  }
  let best: { text: string; hits: number } | undefined;
  for (const text of messages) {
    const hits = words.filter(word => conversationSearchPattern([word]).test(text)).length;
    if (hits > 0 && (best === undefined || hits > best.hits)) {
      best = { text, hits };
    }
  }
  if (best === undefined) {
    return null;
  }
  const first = best.text.search(conversationSearchPattern(words));
  let start = 0;
  if (first > SNIPPET_LEAD) {
    const wordBreak = best.text.indexOf(' ', first - SNIPPET_LEAD);
    start = wordBreak === -1 || wordBreak >= first ? first : wordBreak + 1;
  }
  const rest = best.text.slice(start);
  const line = rest.length > MAX_SNIPPET_LENGTH ? `${rest.slice(0, MAX_SNIPPET_LENGTH)}…` : rest;
  return start > 0 ? `…${line}` : line;
}
